/**
 * @module api/domain/common
 * @description 공통 분류/설정 관련 API
 *   - 지역/급지/요청유형/수리유형/공통코드 조회 및 저장
 *   - 권한 관리, 메뉴 설정, 로그아웃, 토큰 갱신
 *   - 이미지 다운로드
 */
import { getApi, api, noCustomApi, imageDownloadApi } from '@/api/api'
import { buildQueryString } from '@/mixin/mixins'

/** 지역 리스트 조회 */
export const getArea = async () => {
  const url = '/classification/area'
  return await getApi(url)
}

/** 급지 리스트 조회 */
export const getVisitDifficulty = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/classification/visit-difficulty-type/all' + queryString
  return await getApi(url)
}

/** 출장비 리스트 조회 (SURINP-192) */
export const getRepairTravelFee = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/classification/repair-travel-fee/all' + queryString
  return await getApi(url)
}

/** 요청 유형 조회 */
export const getRepairRequestType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/classification/repair-request-types/select-box' + queryString
  return await getApi(url)
}

/** 요청 유형 mainType 조회 (셀렉트박스용) */
export const getRepairRequestMainType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/classification/repair-request-main-type/select-box' + queryString
  return await getApi(url)
}

/** 요청 유형 subType 조회 (셀렉트박스용) */
export const getRepairRequestSubType = async (mainTypeCode) => {
  const url = '/classification/repair-request-sub-type/select-box?mainTypeCode=' + mainTypeCode
  return await getApi(url)
}

/** 공통 코드 서브 조회 */
export const getCommonCodeSub = async (codeMain) => {
  const url = '/classification/code-sub?codeMain=' + codeMain
  return await getApi(url)
}

/** 공통 코드 제조사 조회 */
export const getCommonCodeManufacturer = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/classification/code-sub' + queryString
  return await getApi(url)
}

/** 관리자 권한 조회 */
export const getAuthorityList = async () => {
  const url = '/authorities/use'
  return await getApi(url)
}

/** 메뉴 리스트 조회 */
export const getMenuList = async () => {
  const url = '/auth/menus'
  return await getApi(url)
}

/** 급지 관리 그리드 수정 */
export const updateVisitDifficultyList = async (method, data, options) => {
  const url = '/classification/visit-difficulty-type'
  return await api(url, method, data, options)
}

/** 출장비 관리 그리드 수정 (SURINP-192) */
export const updateRepairTravelFeeList = async (method, data) => {
  const url = '/classification/repair-travel-fee'
  return await api(url, method, data)
}

/**
 * 출장비 기본값 변경 (SURINP-205/207) — 같은 partner의 기존 'Y' → 'N' 전환 후 대상 → 'Y'.
 * 성공 안내는 화면이 「기본 출장비가 변경되었습니다」 하나만 띄운다 — 공통 「저장되었습니다」는 끈다 (SURINP-913)
 */
export const setDefaultRepairTravelFee = async (id) => {
  const url = `/classification/repair-travel-fee/${id}/default`
  return await api(url, 'patch', undefined, { silentSuccess: true })
}

/** 공통 코드 제조사 저장 */
export const saveManufacturer = async (method, data) => {
  const url = '/classification/manufacturer'
  return await api(url, method, data)
}

/** 공통 코드 MAIN 저장 */
export const saveCommonCodeMain = async (method, data) => {
  const url = '/classification/code-main'
  return await api(url, method, data)
}

/** 공통 코드 Sub 저장 */
export const saveCommonCodeSub = async (codeMain, method, data) => {
  const url = `/classification/code-sub/${codeMain}`
  return await api(url, method, data)
}

/** 요청 유형 관리 MAIN 저장 */
export const saveRepairRequestTypeMain = async (method, data) => {
  const url = '/classification/repair-request-main-type'
  return await api(url, method, data)
}

/** 요청 유형 관리 Sub 저장 */
export const saveRepairRequestTypeSub = async (mainTypeCode, method, data) => {
  const url = `/classification/repair-request-sub-type/${mainTypeCode}`
  return await api(url, method, data)
}

/** 수리 유형 관리 MAIN 저장 */
export const saveBrokenTypeMain = async (method, data) => {
  const url = '/classification/broken-main-type'
  return await api(url, method, data)
}

/** 수리 유형 관리 Sub 저장 */
export const saveBrokenTypeSub = async (mainTypeCode, method, data) => {
  const url = `/classification/broken-sub-type/${mainTypeCode}`
  return await api(url, method, data)
}

/** 권한 저장 */
export const saveAuthority = async (method, data) => {
  const url = '/authorities'
  return await api(url, method, data)
}

/** 권한별 메뉴 설정 저장 */
export const saveAuthorityMenu = async (authCode, method, data) => {
  const url = `/authorities/menu/web/${authCode}`
  return await api(url, method, data)
}

/** 로그아웃 */
export const logout = async (method) => {
  const url = '/auth/logout'
  return await noCustomApi(url, method, null)
}

/** 토큰 갱신 */
export const getNewToken = async (method) => {
  const url = '/auth/token/refresh'
  return await noCustomApi(url, method, null)
}

/** 이미지 다운로드 */
export const getImageDownload = async (encoded) => {
  const url = '/image/file-download?ffp=' + encoded
  return await imageDownloadApi(url)
}
